import React, { Component } from "react";
import { Menu, Row, Col, message } from "antd";
import ChecklistForm from "./ChecklistForm";
import { add, update, list, deleteRecord } from "../api/Apis";
import Amplify, { Storage } from "aws-amplify";
import AwsConfig from "../config/AwsConfig";

Amplify.configure(AwsConfig);

class HomeBuddha extends Component {
  state = {
    activeSubKey: "home_health",
    submittedSections: {},
  };

  handleSubMenuItems = (e) => {
    this.setState({ activeSubKey: e.key });
  };

  handleSectionSubmit = async (values, recordId) => {
    try {
      const userDetailsStr = localStorage.getItem("userDetails");
      const userDetails = userDetailsStr ? JSON.parse(userDetailsStr) : {};
      const userId = userDetails.id || userDetails.userId;

      if (!userId) {
        message.error("Please log in to save");
        return;
      }

      let uploadedPhotos = [];
      let photoUrls = [];

      if (values.photos?.length) {
        const uploads = values.photos.map(async (file) => {
          if (file.originFileObj) {
            const keyName = `${userId}_${Date.now()}_${file.name}`;
            const result = await Storage.put(keyName, file.originFileObj);

            const key = result.key || result;

            return {
              key,
              url: `https://${process.env.REACT_APP_S3_BUCKET}.s3.amazonaws.com/public/${key}`,
            };
          }

          const match = file.url?.match(/public\/(.*)$/);
          return {
            key: match ? match[1] : file.name,
            url: file.url,
          };
        });

        const results = await Promise.all(uploads);

        uploadedPhotos = results.map(r => r.key);
        photoUrls = results.map(r => r.url);
      }


      const payload = {
        entityData: {
          userId,
          sectionKey: this.state.activeSubKey,
          description: values.description,
          notes: values.notes,
          brand: values.brand,
          model: values.model,
          rating: values.rating,
          photos: uploadedPhotos,
          photoUrls,
        },
      };

      if (recordId) {
        await update("mybuddha", recordId, payload);
        message.success("Updated successfully");
      } else {
        await add("mybuddha", payload);
        message.success("Saved successfully");
      }

      await this.loadMyBuddha();

    } catch (err) {
      console.error(err);
      message.error("Error saving data");
    }
  };


  handleSectionDelete = async (recordId) => {
    try {
      await deleteRecord("mybuddha", recordId);

      message.success("Deleted successfully");
      await this.loadMyBuddha();
    } catch (err) {
      console.error("Delete error:", err);
      message.error("Error deleting entry");
    }
  };


  componentDidMount() {
    this.loadMyBuddha();
  }

  loadMyBuddha = async () => {
    try {
      const userDetailsStr = localStorage.getItem("userDetails");
      const userDetails = userDetailsStr ? JSON.parse(userDetailsStr) : {};
      const userId = userDetails.id || userDetails.userId || userDetails.userid;
      if (!userId) return;

      const resp = await list("mybuddha", { userId });
      if (resp && resp.success && Array.isArray(resp.data)) {
        const grouped = {};
        resp.data.forEach((entry) => {
          const sectionKey = entry.section || entry.sectionKey || "notes_collaboration";
          if (!grouped[sectionKey]) grouped[sectionKey] = [];
          const normalizedEntry = {
            id: entry.id,
            description: entry.description,
            notes: entry.notes,
            brand: entry.brand,
            model: entry.model,
            rating: entry.rating,
            photos: (entry.photos || []).map((key, i) => ({
              uid: `-${i + 1}`,
              name: key || `photo${i + 1}`,
              status: 'done',
              url: (entry.photoUrls || [])[i],
            })),
          };
          grouped[sectionKey].push(normalizedEntry);
        });

        this.setState({ submittedSections: grouped });
      }
    } catch (err) {
      console.error("Failed to load MyBuddha entries:", err);
    }
  };

  sections = {
    home_health: {
      title: "Home Health & Safety",
      points: [
        "Moisture, humidity & hidden water risk review",
        "Indoor air quality & ventilation balance",
        "HVAC system performance & efficiency check",
        "Electrical safety & capacity overview",
        "Plumbing health & early leak indicators",
        "Fire safety & carbon monoxide readiness",
      ],
    },
    energy_efficiency: {
      title: "Energy Efficiency & Utility Savings",
      points: [
        "Energy waste & utility cost drivers",
        "Insulation effectiveness & thermal gaps",
        "Air sealing & draft reduction opportunities",
        "Window & door efficiency review",
        "Lighting & appliance efficiency suggestions",
        "Smart-home upgrades that reduce bills & effort",
      ],
    },
    maintenance_longevity: {
      title: "Maintenance & Longevity",
      points: [
        "Deferred maintenance risks",
        "System age & replacement planning",
        "Exterior protection & wear indicators",
        "Roof & drainage performance",
        "Foundation & structural stress indicators",
        "Customized seasonal maintenance checklist",
      ],
    },
    stress_reduction: {
      title: "Stress Reduction & Simplicity",
      points: [
        "Top 5 most important priorities (clear focus)",
        "What you don't need to worry about right now",
        "Low-effort, high-impact improvements",
        "Maintenance automation opportunities",
        "Home system organization & simplification ideas",
      ],
    },
    value_wealth: {
      title: "Value, Wealth & Future Proofing",
      points: [
        "Improvements that actually increase home value",
        "What upgrades don’t pay off (avoid wasted money)",
        "Pre-sale readiness insights (even if not selling)",
        "Risk-reduction & insurance-friendly improvements",
        "Energy & sustainability features buyers want",
        "Long-term home wealth strategy overview",
      ],
    },
    notes_collaboration: {
      title: "Notes & Collaborations",
      points: ["Ideas", "Feedback", "Issues", "Tasks", "Summary"],
    },
  };

  render() {
    const { activeSubKey, submittedSections } = this.state;
    const currentSection = this.sections[activeSubKey];

    return (
      <Row style={{ background: "white", minHeight: "100vh" }}>
        <Col span={6} style={{ borderRight: "1px solid #eee" }}>
          <Menu
            mode="vertical"
            selectedKeys={[activeSubKey]}
            onClick={this.handleSubMenuItems}
          >
            <Menu.Item key="home_health">Home Health & Safety</Menu.Item>
            <Menu.Item key="energy_efficiency">Energy Efficiency & Utility Savings</Menu.Item>
            <Menu.Item key="maintenance_longevity">Maintenance & Longevity</Menu.Item>
            <Menu.Item key="stress_reduction">Stress Reduction & Simplicity</Menu.Item>
            <Menu.Item key="value_wealth">Value Wealth & Future Proofing</Menu.Item>
            <Menu.Item key="notes_collaboration">Notes & Collaborations</Menu.Item>
          </Menu>
        </Col>
        <Col span={18} style={{ padding: 24 }}>
          <ChecklistForm
            title={currentSection.title}
            points={currentSection.points}
            onSectionSubmit={this.handleSectionSubmit}
            onSectionDelete={this.handleSectionDelete}
            submittedData={submittedSections[activeSubKey] || []}
          />
        </Col>
      </Row>
    );
  }
}

export default HomeBuddha;